import { useMemo } from 'react';
import { Button, DatePicker, Input, Segmented, Select, Typography, theme } from 'antd';
import { ClearOutlined, DownloadOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import { FLOW_EXECUTION_STATUSES } from '@/api/logs';
import { getConversationOverviewPeriods } from '@/components/conversation-overview';
import { TenantSelect } from '@/components/tenant';
import { FilterBar } from '@/components/ui/FilterBar';
import { HorizontalScroll } from '@/components/ui/HorizontalScroll';
import type { AdminLogsSearch } from './searchSchema';

const { RangePicker } = DatePicker;
const { Text } = Typography;

export interface LogsFiltersProps {
  values: AdminLogsSearch;
  /** Tenant picker for the global page (executions are filtered by client ID) */
  showTenantFilter: boolean;
  /** Audit tab scope options (tenant level and its companies); hidden when empty */
  auditScopeOptions: { value: string; label: string }[];
  hasActiveFilters: boolean;
  isFetching: boolean;
  lastUpdatedAt: number | undefined;
  exportDisabled: boolean;
  onChange: (patch: Partial<AdminLogsSearch>) => void;
  onClear: () => void;
  onRefresh: () => void;
  onExport: () => void;
}

/**
 * Filter bar of the admin logs view: period, status (executions), search (audit), tenant or
 * audit scope, plus clear, refresh and CSV export.
 */
export function LogsFilters({
  values,
  showTenantFilter,
  auditScopeOptions,
  hasActiveFilters,
  isFetching,
  lastUpdatedAt,
  exportDisabled,
  onChange,
  onClear,
  onRefresh,
  onExport
}: LogsFiltersProps) {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const periodOptions = useMemo(() => getConversationOverviewPeriods(t), [t]);
  const isAudit = values.tab === 'audit';
  const customRange: [Dayjs, Dayjs] | null = values.period === 'custom' && values.from && values.to ? [dayjs(values.from), dayjs(values.to)] : null;

  return (
    <FilterBar
      actions={
        <>
          {hasActiveFilters && (
            <Button icon={<ClearOutlined />} onClick={onClear}>
              {t('logs.filters.clear')}
            </Button>
          )}
          {lastUpdatedAt ? (
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('logs.lastUpdated', { time: dayjs(lastUpdatedAt).format('HH:mm:ss') })}
            </Text>
          ) : null}
          <Button icon={<ReloadOutlined spin={isFetching} />} onClick={onRefresh}>
            {t('logs.refresh')}
          </Button>
          <Button icon={<DownloadOutlined />} onClick={onExport} disabled={exportDisabled}>
            {t('logs.export')}
          </Button>
        </>
      }
    >
      <HorizontalScroll>
        <Segmented
          options={periodOptions}
          value={values.period}
          onChange={(value) =>
            onChange(value === 'custom' ? { period: 'custom' } : { period: value as AdminLogsSearch['period'], from: undefined, to: undefined, page: 1 })
          }
        />
      </HorizontalScroll>
      {values.period === 'custom' && (
        <RangePicker
          style={{ flex: '1 1 240px', minWidth: 0, maxWidth: 320 }}
          value={customRange}
          onChange={(dates) =>
            onChange(
              dates?.[0] && dates?.[1]
                ? { from: dates[0].startOf('day').toISOString(), to: dates[1].endOf('day').toISOString(), page: 1 }
                : { from: undefined, to: undefined, page: 1 }
            )
          }
        />
      )}

      {showTenantFilter && (
        <TenantSelect
          allowClear
          style={{ flex: '1 1 200px', minWidth: 160 }}
          placeholder={t('logs.filters.allTenants')}
          value={values.clientId}
          onChange={(value) => onChange({ clientId: value, auditScope: undefined, page: 1 })}
        />
      )}

      {!isAudit && (
        <Select
          allowClear
          style={{ flex: '0 1 180px', minWidth: 150 }}
          placeholder={t('logs.filters.allStatuses')}
          value={values.status}
          options={FLOW_EXECUTION_STATUSES.map((status) => ({ value: status, label: t(`logs.status.${status}`) }))}
          onChange={(value) => onChange({ status: value, page: 1 })}
        />
      )}

      {isAudit && auditScopeOptions.length > 0 && (
        <Select
          style={{ flex: '1 1 200px', minWidth: 160 }}
          value={values.auditScope ?? auditScopeOptions[0].value}
          options={auditScopeOptions}
          onChange={(value) => onChange({ auditScope: value, page: 1 })}
        />
      )}

      {isAudit && (
        <Input.Search
          // Remount when the URL value changes (e.g. "Clear filters") so the box shows the active search
          key={values.search ?? ''}
          allowClear
          style={{ flex: '1 1 220px', minWidth: 160 }}
          placeholder={t('logs.filters.searchAudit')}
          prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
          defaultValue={values.search}
          onSearch={(value) => onChange({ search: value.trim() || undefined, page: 1 })}
        />
      )}
    </FilterBar>
  );
}
